import { useState } from 'react';
import type { OrderSide } from '@/analytics';
import { cn } from '@/lib/utils';
import { centsToPercent } from '@/lib/binary-market';
import type { EventDetailMarketDto, MarketOptionDto } from '@/lib/models';
import { MarketOutcomePricesSubscriber, optionCents, useOutcomePrices, type OutcomePriceMap } from './useOutcomePrices';

/** How many candidate cards a MULTI market shows before "Show all" (Figma multi-answer). */
const MULTI_VISIBLE = 3;

export interface MarketBuyEntryProps {
  /** Event id (analytics attribution). */
  eventId: string;
  /** The event's markets — each carries its own `renderKind` + `options`. */
  markets: EventDetailMarketDto[];
  /** Opens the buy popup pre-set to the chosen option + side (§5.3). */
  onOpenBuy: (option: MarketOptionDto, side: OrderSide) => void;
}

/**
 * Market-page buy entry points (ARC-82 §5.3). Renders straight from the
 * backend-resolved shape — no client classification:
 * - a **BINARY** market → a Yes/No bar (its single option),
 * - a **MULTI** market → one card per candidate option,
 * - a multi-market event → each market stacked, labelled.
 *
 * Each button opens the buy popup pre-set to that option's Yes/No buy target.
 * Live `%` + payout overlay the seed via Centrifugo, keyed per outcome.
 */
export function MarketBuyEntry({ markets, onOpenBuy }: MarketBuyEntryProps) {
  const { prices, setPrice } = useOutcomePrices();

  function handleTap(option: MarketOptionDto, side: OrderSide) {
    onOpenBuy(option, side);
  }

  const multiMarket = markets.length > 1;

  return (
    <div className="flex flex-col gap-3">
      {markets.map((m) => (
        <MarketOutcomePricesSubscriber key={m.id} marketId={m.id} externalMarketId={m.externalMarketId} onPrice={setPrice} />
      ))}
      {markets.map((m) => (
        <MarketEntry key={m.id} market={m} multiMarket={multiMarket} prices={prices} onTap={handleTap} />
      ))}
    </div>
  );
}

/** One market's entry — a Yes/No bar (BINARY) or a stack of candidate cards (MULTI). */
function MarketEntry({
  market,
  multiMarket,
  prices,
  onTap,
}: {
  market: EventDetailMarketDto;
  multiMarket: boolean;
  prices: OutcomePriceMap;
  onTap: (option: MarketOptionDto, side: OrderSide) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  if (market.options.length === 0) return null;
  const heading =
    multiMarket && market.displayName ? (
      <span className="text-sm font-semibold text-primary">{market.displayName}</span>
    ) : null;

  if (market.renderKind === 'BINARY') {
    const option = market.options[0];
    const cents = optionCents(option, prices);
    // md+: a labelled market becomes a compact row (label left, buttons right);
    // a lone Yes/No pair (single-binary page) is centered and width-capped so the
    // buttons don't stretch across the full desktop content column.
    return (
      <div className={heading ? 'flex flex-col gap-1.5 md:flex-row md:items-center md:gap-4' : 'flex flex-col gap-1.5'}>
        {heading && <div className="min-w-0 md:flex-1 md:truncate">{heading}</div>}
        <div className={heading ? 'flex gap-2 md:w-[336px] md:shrink-0' : 'flex w-full gap-2 md:mx-auto md:max-w-xl'}>
          <SideEntry side="yes" cents={cents.yes} onTap={() => onTap(option, 'yes')} />
          <SideEntry side="no" cents={cents.no} onTap={() => onTap(option, 'no')} />
        </div>
      </div>
    );
  }

  // MULTI — rank candidates by LIVE chance (so a price tick re-orders the list,
  // favourites first), then cap to the top few with an expand toggle.
  const ranked = [...market.options].sort((a, b) => (optionCents(b, prices).yes ?? -1) - (optionCents(a, prices).yes ?? -1));
  const shown = showAll ? ranked : ranked.slice(0, MULTI_VISIBLE);
  const hidden = ranked.length - shown.length;
  return (
    <div className="flex flex-col gap-2">
      {heading}
      {/* One card per candidate on every breakpoint. Mobile stacks the buttons
          under the label; md+ flattens each card into a compact row — label +
          live chance on the left, fixed-width Yes/No buttons on the right. */}
      <div className="flex flex-col gap-2">
        {shown.map((option) => {
          const cents = optionCents(option, prices);
          const yesPct = centsToPercent(cents.yes);
          return (
            <div
              key={option.id}
              className="flex flex-col gap-2 rounded-card border border-border bg-surface p-3 md:flex-row md:items-center md:gap-4 md:px-4"
            >
              <div className="flex min-w-0 items-center justify-between gap-3 md:flex-1">
                <span className="truncate text-sm font-semibold text-primary md:text-base">{option.label ?? 'Option'}</span>
                <span className="shrink-0 text-sm font-semibold text-info md:text-base">
                  {yesPct == null ? '—' : `${yesPct}%`}
                </span>
              </div>
              <div className="flex gap-2 md:w-[336px] md:shrink-0">
                <SideEntry side="yes" cents={cents.yes} onTap={() => onTap(option, 'yes')} />
                <SideEntry side="no" cents={cents.no} onTap={() => onTap(option, 'no')} />
              </div>
            </div>
          );
        })}
      </div>
      {(hidden > 0 || showAll) && (
        <button
          type="button"
          onClick={() => setShowAll((s) => !s)}
          className="self-center text-sm font-semibold text-purple underline"
        >
          {showAll ? 'Show less' : `More Markets (${market.optionCount})`}
        </button>
      )}
    </div>
  );
}

/**
 * One Yes/No buy button with its live chance and a `Payout $X.XX` line below.
 * Styled as the subtle green/red chance chips from the lobby snake preview
 * (SnakeLobbyCard), made tappable.
 */
function SideEntry({ side, cents, onTap }: { side: OrderSide; cents: number | null; onTap: () => void }) {
  const pct = centsToPercent(cents);
  return (
    <div className="flex flex-1 flex-col items-center gap-1">
      <button
        type="button"
        onClick={onTap}
        className={cn(
          'flex h-10 w-full cursor-pointer items-center justify-center gap-1 rounded-pill transition-opacity hover:opacity-80 active:opacity-70',
          side === 'yes' ? 'bg-chip-green text-up' : 'bg-chip-red text-down',
        )}
      >
        <span className="text-[16px] font-semibold tracking-[-0.32px]">{side === 'yes' ? 'Yes' : 'No'}</span>
        <span className="text-[16px] font-semibold tracking-[-0.32px]">{pct == null ? '—' : `${pct}%`}</span>
      </button>
    </div>
  );
}
